import { Injectable, OnDestroy, effect, untracked } from '@angular/core';
import { Observable, Subject, Subscription, filter, map } from 'rxjs';
import { UserActionDto } from '../../models/dtos/notification/notification-dtos';
import { SessionService } from '../account-service/session.service';
import { RealtimeService } from '../realtime/realtime.service';
import {
  SavedUserActionNotification, SavedUserActionNotificationOf, SavedUserActionType, SystemNotification,
  SystemNotificationOf, SystemNotificationType, readSavedUserActionNotification, readSavedUserActionOfType,
  readSystemNotification
} from './notification-messages';

// Public dispatch point for validated ephemeral system events and API-confirmed saved user actions.
// SignalR remains a transport and hint source; the UserActionInboxService owns authorised reads.
@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  // Private variables

  private readonly subscriptions = new Subscription();
  private readonly systemEvents = new Subject<SystemNotification>();
  private readonly savedUserActions = new Subject<SavedUserActionNotification>();
  private readonly userActionsInvalidated = new Subject<void>();
  private readonly connectionRecovered = new Subject<void>();
  private readonly seenSavedActionIds = new Set<string>();

  private identity = '';
  private savedActionsInitialised = false;
  private destroyed = false;

  // Constructors

  constructor(private readonly session: SessionService, private readonly realtime: RealtimeService) {
    effect(() => {
      const identity = this.sessionIdentity();
      untracked(() => this.switchIdentity(identity));
    });

    this.subscriptions.add(this.realtime.notifications.subscribe(envelope => {
      if (!this.currentIdentity())
        return;

      const event = readSystemNotification(envelope);

      if (event)
        this.systemEvents.next(event);
    }));

    this.subscriptions.add(this.realtime.userActionsChanged.subscribe(() => {
      if (this.currentIdentity())
        this.userActionsInvalidated.next();
    }));

    this.subscriptions.add(this.realtime.recovered.subscribe(() => {
      if (this.currentIdentity())
        this.connectionRecovered.next();
    }));
  }

  // Public methods

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.subscriptions.unsubscribe();
    this.seenSavedActionIds.clear();
    this.systemEvents.complete();
    this.savedUserActions.complete();
    this.userActionsInvalidated.complete();
    this.connectionRecovered.complete();
  }

  public listenForSystemEvents(): Observable<SystemNotification> {
    return this.systemEvents.asObservable();
  }

  public listenForSystemEvent<T extends SystemNotificationType>(type: T): Observable<SystemNotificationOf<T>> {
    return this.systemEvents.pipe(
      filter(event => event.type === type),
      map(event => event as SystemNotificationOf<T>)
    );
  }

  public listenForUserActions(): Observable<SavedUserActionNotification> {
    return this.savedUserActions.asObservable();
  }

  public listenForUserAction<T extends SavedUserActionType>(type: T): Observable<SavedUserActionNotificationOf<T>> {
    return this.savedUserActions.pipe(
      map(notification => readSavedUserActionOfType(notification, type)),
      filter((notification): notification is SavedUserActionNotificationOf<T> => notification !== undefined)
    );
  }

  // These are refresh hints. Neither contains an authoritative saved UserAction payload.
  public listenForUserActionInvalidations(): Observable<void> {
    return this.userActionsInvalidated.asObservable();
  }

  public listenForConnectionRecovery(): Observable<void> {
    return this.connectionRecovered.asObservable();
  }

  // Only call after the inbox has confirmed a complete, current, authorised account + club read.
  // The first saved snapshot establishes a baseline; previously pending actions remain in the inbox
  // but are not shown as new growls just because the user signed in or reloaded the page.
  public reconcileSavedUserActions(actions: readonly UserActionDto[], accountId: string, sessionVersion: number): void {
    const identity = this.currentIdentity();

    if (!identity || identity !== `${accountId}:${sessionVersion}`)
      return;

    const firstRead = !this.savedActionsInitialised;
    this.savedActionsInitialised = true;

    for (const action of actions)
    {
      const notification = readSavedUserActionNotification(action);

      if (!notification || action.dismissed || action.dismissedForAll)
        continue;

      const key = action.id.toLowerCase();

      if (this.seenSavedActionIds.has(key))
        continue;

      this.seenSavedActionIds.add(key);

      if (!firstRead)
        this.savedUserActions.next(notification);
    }
  }

  // Private methods

  private sessionIdentity(): string {
    const account = this.session.account();

    return this.session.status() === 'authenticated' && account?.id
      ? `${account.id}:${this.session.version}` : '';
  }

  private currentIdentity(): string {
    if (this.destroyed)
      return '';

    const identity = this.sessionIdentity();
    this.switchIdentity(identity);
    return identity;
  }

  private switchIdentity(identity: string): void {
    if (identity === this.identity)
      return;

    this.identity = identity;
    this.savedActionsInitialised = false;
    this.seenSavedActionIds.clear();
  }
}
